import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Import pages
import LandingPage from "@/pages/LandingPage";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPageNew";
import VerifyEmailPage from "@/pages/VerifyEmailPageNew";
import AdminPanel from "@/pages/AdminPanelNew";
import RegistrationFlowNew from "@/pages/RegistrationFlowNew";
import EventsListPage from "@/pages/EventsListPage";
import EventRegistrationFlow from "@/pages/EventRegistrationFlow";
import EventsPage from "@/pages/EventsPage";
import TeamsPage from "@/pages/TeamsPage";
import TokenPurchasePage from "@/pages/TokenPurchasePage";
import TokenHistoryPage from "@/pages/TokenHistoryPage";
import GalleryPage from "@/pages/GalleryPage";
import VotingPage from "@/pages/VotingPage";
import TicketsPage from "@/pages/TicketsPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import NotificationsPage from "@/pages/NotificationsPage";
import BerkasTambahanPage from "@/pages/BerkasTambahanPage";
import ProfilePage from "@/pages/ProfilePage";
import BeritaListPage from "@/pages/BeritaListPage";
import BeritaDetailPage from "@/pages/BeritaDetailPage";
import FAQPage from "@/pages/FAQPage";
import KlasemenPage from "@/pages/KlasemenPage";
import JadwalPage from "@/pages/JadwalPage";
import HasilPage from "@/pages/HasilPage";
import TopSkorPage from "@/pages/TopSkorPage";
import PertandinganPage from "@/pages/PertandinganPage";
import VideoPage from "@/pages/VideoPage";
import TopupPage from "@/pages/TopupPage";
import StatistikPage from "@/pages/StatistikPage";
import BottomNavigation from "@/components/BottomNavigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/topup"
              element={
                <ProtectedRoute>
                  <TopupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <RegistrationFlowNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:eventId/register"
              element={
                <ProtectedRoute>
                  <EventRegistrationFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <ProtectedRoute>
                  <TeamsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tokens/purchase"
              element={
                <ProtectedRoute>
                  <TokenPurchasePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tokens/history"
              element={
                <ProtectedRoute>
                  <TokenHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gallery"
              element={
                <ProtectedRoute>
                  <GalleryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/voting"
              element={
                <ProtectedRoute>
                  <VotingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets"
              element={
                <ProtectedRoute>
                  <TicketsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/berkas/:registrationId"
              element={
                <ProtectedRoute>
                  <BerkasTambahanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/berita"
              element={<BeritaListPage />}
            />
            <Route
              path="/berita/:id"
              element={<BeritaDetailPage />}
            />
            <Route
              path="/faq"
              element={<FAQPage />}
            />
            <Route
              path="/video"
              element={<VideoPage />}
            />
            <Route
              path="/klasemen"
              element={
                <ProtectedRoute>
                  <KlasemenPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pertandingan"
              element={
                <ProtectedRoute>
                  <PertandinganPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jadwal"
              element={
                <ProtectedRoute>
                  <JadwalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hasil"
              element={
                <ProtectedRoute>
                  <HasilPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/top-skor"
              element={
                <ProtectedRoute>
                  <TopSkorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistik"
              element={
                <ProtectedRoute>
                  <StatistikPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Global Bottom Navigation */}
          <BottomNavigation />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
